/**
 * Front desk operations API (Admin + Front Desk roles). Base URL from client.
 */
import {
  api,
  getBaseUrl,
  getStoredToken,
  apiHeaders,
  openPdfBlobInNewTab,
  rethrowAsUserFriendlyNetworkError,
} from './client';

function qs(params) {
  if (params == null || typeof params !== 'object' || Array.isArray(params)) {
    return '';
  }
  const u = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      u.set(k, String(v));
    }
  });
  const s = u.toString();
  return s ? `?${s}` : '';
}

export const frontDeskApi = {
  summary: (p) => api.get(`/front-desk/summary${qs(p)}`),
  arrivals: (p) => api.get(`/front-desk/arrivals${qs(p)}`),
  departures: (p) => api.get(`/front-desk/departures${qs(p)}`),
  inHouse: (p) => api.get(`/front-desk/in-house${qs(p)}`),
  roomRack: (p) => api.get(`/front-desk/room-rack${qs(p)}`),
  bookingPlatforms: () => api.get('/front-desk/booking-platforms'),
  properties: (p) => api.get(`/front-desk/properties${qs(p)}`),
  apartments: (propertyId) => api.get(`/front-desk/apartments${qs({ property_id: propertyId })}`),
  team: (propertyId, params) => api.get(`/front-desk/properties/${propertyId}/team${qs(params || {})}`),
  staffPermissionGroups: () => api.get('/front-desk/staff-permission-groups'),
  staffJobTitles: () => api.get('/front-desk/staff-job-titles'),
  staffPermissionRoles: (params) => api.get(`/front-desk/staff-permission-roles${qs(params || {})}`),
  createStaffPermissionRole: (body) => api.post('/front-desk/staff-permission-roles', body),
  updateStaffPermissionRole: (id, body) => api.put(`/front-desk/staff-permission-roles/${id}`, body),
  deleteStaffPermissionRole: (id) => api.delete(`/front-desk/staff-permission-roles/${id}`),
  surchargeTypes: () => api.get('/front-desk/surcharge-types'),
  apartmentPrices: (propertyId, apartmentId) =>
    api.get(`/front-desk/properties/${propertyId}/apartment-prices${qs({ apartment_id: apartmentId })}`),
  discounts: (propertyId) => api.get(`/front-desk/properties/${propertyId}/discounts`),
  createDiscount: (propertyId, body) => api.post(`/front-desk/properties/${propertyId}/discounts`, body),
  updateDiscount: (propertyId, id, body) => api.put(`/front-desk/properties/${propertyId}/discounts/${id}`, body),
  deleteDiscount: (propertyId, id) => api.delete(`/front-desk/properties/${propertyId}/discounts/${id}`),
  surcharges: (propertyId) => api.get(`/front-desk/properties/${propertyId}/surcharges`),
  createSurcharge: (propertyId, body) => api.post(`/front-desk/properties/${propertyId}/surcharges`, body),
  updateSurcharge: (propertyId, id, body) => api.put(`/front-desk/properties/${propertyId}/surcharges/${id}`, body),
  deleteSurcharge: (propertyId, id) => api.delete(`/front-desk/properties/${propertyId}/surcharges/${id}`),
  blockedDates: (propertyId) => api.get(`/front-desk/properties/${propertyId}/blocked-dates`),
  createBlockedDate: (propertyId, body) => api.post(`/front-desk/properties/${propertyId}/blocked-dates`, body),
  updateBlockedDate: (propertyId, id, body) => api.put(`/front-desk/properties/${propertyId}/blocked-dates/${id}`, body),
  deleteBlockedDate: (propertyId, id) => api.delete(`/front-desk/properties/${propertyId}/blocked-dates/${id}`),
  createStaffAssignment: (propertyId, body) =>
    api.post(`/front-desk/properties/${propertyId}/staff-assignments`, body),
  updateStaffAssignment: (assignmentId, body) =>
    api.put(`/front-desk/staff-assignments/${assignmentId}`, body),
  deleteStaffAssignment: (assignmentId) => api.delete(`/front-desk/staff-assignments/${assignmentId}`),
  /** @param {string} q @param {AbortSignal} [signal] */
  searchGuests: (q, signal) =>
    api.get(`/front-desk/guests/search${qs({ q })}`, undefined, signal ? { signal } : {}),
  bookings: (p) => api.get(`/front-desk/bookings${qs(p)}`),
  booking: (id) => api.get(`/front-desk/bookings/${id}`),
  createBooking: (body) => api.post('/front-desk/bookings', body),
  updateBooking: (id, body) => api.patch(`/front-desk/bookings/${id}`, body),
  approve: (id) => api.post(`/front-desk/bookings/${id}/approve`, {}),
  markPaid: (id) => api.post(`/front-desk/bookings/${id}/mark-paid`, {}),
  /** @param {{ refund_amount: number, note?: string }} body */
  guestRefund: (id, body) => api.post(`/front-desk/bookings/${id}/guest-refund`, body),
  /** @param {{ kind: 'refund'|'forfeit', amount: number, note?: string }} body */
  cautionAdjustment: (id, body) => api.post(`/front-desk/bookings/${id}/caution-adjustment`, body),
  /** @param {Record<string, string|number>|FormData} [body] optional ID number / document (multipart) */
  checkIn: (id, body) => api.post(`/front-desk/bookings/${id}/check-in`, body ?? {}),
  checkOut: (id, body) => api.post(`/front-desk/bookings/${id}/check-out`, body ?? {}),
  uploadGuestDocuments: (id, formData) => api.post(`/front-desk/bookings/${id}/guest-documents`, formData),
  deleteBooking: (id) => api.delete(`/front-desk/bookings/${id}`),
  reportsMeta: () => api.get('/front-desk/reports'),
  reportJson: (type, p) => api.get(`/front-desk/reports/${type}${qs({ ...p, format: 'json' })}`),
  activityLog: (p) => api.get(`/front-desk/activity-log${qs(p)}`),
  expenseTypes: () => api.get('/front-desk/finance/expense-types'),
  purchaseTypes: () => api.get('/front-desk/finance/purchase-types'),
  expenses: (propertyId, params) =>
    api.get(`/front-desk/properties/${propertyId}/expenses${qs(params || {})}`),
  createExpense: (propertyId, body) => api.post(`/front-desk/properties/${propertyId}/expenses`, body),
  updateExpense: (id, body) => api.put(`/front-desk/expenses/${id}`, body),
  deleteExpense: (id) => api.delete(`/front-desk/expenses/${id}`),
  purchases: (propertyId, params) =>
    api.get(`/front-desk/properties/${propertyId}/purchases${qs(params || {})}`),
  createPurchase: (propertyId, body) => api.post(`/front-desk/properties/${propertyId}/purchases`, body),
  updatePurchase: (id, body) => api.put(`/front-desk/purchases/${id}`, body),
  deletePurchase: (id) => api.delete(`/front-desk/purchases/${id}`),
  payroll: (propertyId, params) =>
    api.get(`/front-desk/properties/${propertyId}/payroll${qs(params || {})}`),
  createPayroll: (propertyId, body) => api.post(`/front-desk/properties/${propertyId}/payroll`, body),
  deletePayroll: (id) => api.delete(`/front-desk/payroll-entries/${id}`),
  /** Multipart FormData must use POST (PHP does not parse multipart bodies for PATCH). JSON may use patchStaffKycJson. */
  patchStaffKyc: (assignmentId, body) =>
    api.post(`/front-desk/staff-assignments/${assignmentId}/kyc`, body),
  patchStaffKycJson: (assignmentId, body) =>
    api.patch(`/front-desk/staff-assignments/${assignmentId}/kyc`, body),
};

/**
 * Export report as PDF (in-app viewer) or XLSX (file download).
 * @param {string} type occupancy|revenue|ledger|movements|profit_and_loss|…
 * @param {Record<string, string|number>} params from, to, optional property_id
 * @param {'pdf'|'xlsx'} format
 * @returns {Promise<'viewer'|'download'|undefined>} for PDF, how it was shown; undefined for Excel
 */
export async function downloadFrontDeskReport(type, params, format) {
  const base = getBaseUrl();
  const path = `/front-desk/reports/${type}${qs({ ...params, format })}`;
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  const token = getStoredToken();
  if (!token) throw new Error('Please sign in to download reports.');
  let res;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: {
        ...apiHeaders(token),
        Accept: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
  } catch (err) {
    rethrowAsUserFriendlyNetworkError(err, { url });
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || res.statusText || 'Download failed');
  }
  const blob = await res.blob();
  if (format === 'pdf') {
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="?([^";]+)"?/i);
    const filename = m ? m[1] : `luxistt-report-${type}.pdf`;
    return openPdfBlobInNewTab(blob, { filename });
  }
  const ext = 'xlsx';
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="?([^";]+)"?/i);
  const filename = m ? m[1] : `luxistt-report-${type}.${ext}`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

/**
 * Fetch payroll slip PDF (front desk). Returns a Blob; use openPdfBlobInNewTab from ./client.
 * @param {number|string} payrollEntryId
 * @returns {Promise<Blob>}
 */
export async function downloadFrontDeskPayrollSlip(payrollEntryId) {
  const path = `/front-desk/payroll-entries/${payrollEntryId}/slip`;
  const url = `${getBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
  const token = getStoredToken();
  if (!token) {
    throw new Error('Please sign in to download this payslip.');
  }
  let res;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (err) {
    rethrowAsUserFriendlyNetworkError(err, { url });
  }
  const contentType = res.headers.get('Content-Type') || '';
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.message || res.statusText || 'Could not download payslip');
    err.status = res.status;
    throw err;
  }
  if (!contentType.includes('pdf')) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'We could not open that document. Please try again.');
  }
  return res.blob();
}
